/**
 * Auth Store — handles authentication, session tokens, and user profile.
 * Domain: Authentication & Identity
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import axios from 'axios'
import api from '@/services/api'

export const useAuthStore = defineStore('auth', () => {
  // ─── State ───────────────────────────────────────────────────────────────
  const user = ref(null)
  const token = ref(localStorage.getItem('access_token'))
  const refreshToken = ref(localStorage.getItem('refresh_token'))
  const loading = ref(false)

  // ─── Getters ─────────────────────────────────────────────────────────────
  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const isAdmin = computed(() => user.value?.is_admin || false)
  const isBoardMember = computed(() => user.value?.is_board_member || false)
  const isPlainUser = computed(() => !isAdmin.value && !isBoardMember.value)
  const fullName = computed(() =>
    user.value ? `${user.value.first_name} ${user.value.last_name}` : ''
  )

  // ─── Actions ─────────────────────────────────────────────────────────────
  const login = async (credentials) => {
    loading.value = true
    try {
      const response = await api.post('/auth/login', credentials)

      // 2FA required — pass back to the caller to handle the OTP step
      if (response.data.requires_2fa) {
        return {
          success: false,
          requires_2fa: true,
          pre_auth_token: response.data.pre_auth_token,
          message: response.data.message
        }
      }

      const { access_token, refresh_token, user: userData } = response.data
      setTokens(access_token, refresh_token)
      user.value = userData
      return { success: true, user: userData }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Login failed'
      }
    } finally {
      loading.value = false
    }
  }

  const verify2fa = async ({ pre_auth_token, code }) => {
    loading.value = true
    try {
      const response = await api.post('/auth/verify-2fa', { pre_auth_token, code })
      const { access_token, refresh_token, user: userData } = response.data
      setTokens(access_token, refresh_token)
      user.value = userData
      return { success: true, user: userData }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Verification failed'
      }
    } finally {
      loading.value = false
    }
  }

  const register = async (userData) => {
    loading.value = true
    try {
      const response = await api.post('/auth/register', userData)
      return { success: true, user: response.data.user }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Registration failed'
      }
    } finally {
      loading.value = false
    }
  }

  const logout = async () => {
    try {
      if (token.value) await api.post('/auth/logout')
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      clearAuth()
    }
  }

  const getCurrentUser = async () => {
    if (!token.value) return null
    try {
      const response = await api.get('/auth/profile')
      user.value = response.data.user
      return user.value
    } catch (error) {
      clearAuth()
      throw error
    }
  }

  const updateProfile = async (profileData) => {
    loading.value = true
    try {
      const response = await api.put('/auth/profile', profileData)
      user.value = response.data.user
      return { success: true, user: user.value }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Profile update failed'
      }
    } finally {
      loading.value = false
    }
  }

  const changePassword = async (passwordData) => {
    loading.value = true
    try {
      await api.post('/auth/change-password', passwordData)
      return { success: true, message: 'Password changed successfully' }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Password change failed'
      }
    } finally {
      loading.value = false
    }
  }

  const refreshAccessToken = async () => {
    if (!refreshToken.value) return false
    try {
      const refreshApi = axios.create({ baseURL: api.defaults.baseURL })
      const response = await refreshApi.post('/auth/refresh', {}, {
        headers: { Authorization: `Bearer ${refreshToken.value}` }
      })
      setTokens(response.data.access_token, refreshToken.value)
      return true
    } catch {
      clearAuth()
      return false
    }
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────
  const setTokens = (accessToken, refreshTokenValue) => {
    token.value = accessToken
    refreshToken.value = refreshTokenValue
    localStorage.setItem('access_token', accessToken)
    localStorage.setItem('refresh_token', refreshTokenValue)
    api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
  }

  const clearAuth = () => {
    user.value = null
    token.value = null
    refreshToken.value = null
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    delete api.defaults.headers.common['Authorization']
  }

  // Initialize Authorization header if token exists on store creation
  if (token.value) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token.value}`
  }

  return {
    // State
    user, token, refreshToken, loading,
    // Getters
    isAuthenticated, isAdmin, isBoardMember, isPlainUser, fullName,
    // Actions
    login, verify2fa, register, logout, getCurrentUser,
    updateProfile, changePassword, refreshAccessToken,
    setTokens, clearAuth
  }
})
